import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form7004DueDate = () => {
  return (
    <AppLayout
      meta={
        <Meta
          titleTemplate="Form 7004 Due Date for the 2025 Tax Year"
          description="Ensure timely extension filing for business taxes by the deadline to avoid penalties. File Form 7004 with the IRS to receive a 6-month extension. E-file now"
          keywords=""
          canonical="resources/tax-extension/form-7004-due-dates/"
          ogType="article"
          jsonLdPricing="17.99"
          jsonLdAggregateRatingValue="4.95"
          jsonLdAggregateRatingCount="10859"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 7004</span>
          </span>{' '}
          Due Dates
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Understand the Form 7004 due dates and file on time to stay compliant
          with the IRS requirements.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=7004duedates`}
          >
            Get an Extension Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-extension/form-7004-due-dates/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>
                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-20T09:00:00-05:00"
                      >
                        Aug 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        Aug 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 7004 Due Dates at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}>Form 7004</span> is
                        used to request an{' '}
                        <span style={{ fontWeight: 600 }}>
                          automatic extension of up to 6 months
                        </span>{' '}
                        to file certain business income tax, information, and
                        other IRS returns.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        The extension applies only to the{' '}
                        <span style={{ fontWeight: 600 }}>filing deadline</span>{' '}
                        and does not extend the time to pay any tax due, which
                        must generally be paid by the{' '}
                        <span style={{ fontWeight: 600 }}>
                          original return due date
                        </span>
                        .
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        The{' '}
                        <span style={{ fontWeight: 600 }}>
                          Form 7004 filing deadline
                        </span>{' '}
                        is generally the{' '}
                        <span style={{ fontWeight: 600 }}>
                          original due date
                        </span>{' '}
                        of the business tax return, with the extended due date
                        varying based on the type of return being filed.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        Businesses operating on a fiscal tax year must generally{' '}
                        <a
                          href="https://www.taxzerone.com/tax-extension/efile-irs-form-7004-business-tax-extension-online/"
                          style={{ color: '#0202ff', textDecoration: 'none' }}
                        >
                          file Form 7004
                        </a>{' '}
                        by the{' '}
                        <span style={{ fontWeight: 600 }}>
                          15th day of the third month
                        </span>{' '}
                        after the close of their tax year to request an
                        extension.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        Late payment of taxes may result in{' '}
                        <span style={{ fontWeight: 600 }}>
                          penalties and interest
                        </span>
                        , even when a Form 7004 extension has been approved.
                      </span>
                    </li>
                  </Box>
                </Box>
                {/* <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 7004?
                </Typography> */}
                {/* <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 1 }}
                > */}
                <Box>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Filing business income tax can be a complex and
                    time-consuming process, often requiring a significant amount
                    of time to gather the necessary information. Occasionally,
                    businesses may struggle to compile the required data and
                    miss the filing deadline. In such cases, these businesses
                    may require extra time to properly prepare and submit their
                    income tax return.
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Federal Form 7004 serves as a solution for businesses facing
                    this challenge. It allows them to request an extension of
                    time to file their income tax return, granting the necessary
                    additional time to prepare without incurring penalties.
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 4 }}>
                    In this comprehensive guide, we will explore the intricacies
                    of Form 7004, its purpose, the deadlines associated with it,
                    and the filing procedures for the 2025 tax year.
                  </Typography>
                </Box>
                <Box sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }} />
                {/* </Stack> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is Form 7004?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 7004, officially titled &quot;Application for Automatic
                  Extension of Time To File Certain Business Income Tax,
                  Information, and Other Returns,&quot; is a crucial IRS form
                  for businesses seeking additional time to complete and submit
                  their business income tax returns. This business extension
                  applies to various tax forms, including C-corporations,
                  S-corporations, partnerships, multiple-member LLCs, trusts,
                  estates and others.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  By{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-7004-business-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    filing Form 7004
                  </Link>
                  , businesses can get an extension period of{' '}
                  <b>up to 6 months.</b>
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  It is important to note that the extension corporate tax Form
                  7004 does not grant an extension of time to pay any taxes
                  owed; it only extends the deadline for filing the return.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the deadline for filing Form 7004?
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  For most businesses operating on a regular calendar year, the
                  business tax extension deadline is same as the original due
                  date of the tax return.
                </Typography>
                <List sx={{ mb: 0 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      For corporations, the standard deadline is typically March
                      16th.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      For partnerships, it is usually April 15th.
                    </ListItemText>
                  </ListItem>
                </List>
                {/* <Alert severity="warning" icon={false} sx={{ mb: 5, mt: 0 }}>
                  <b> Note:</b> Note: If your business is required to file Form
                  1041-A, Trust Accumulation of Charitable Amounts, and you want
                  an extension of time, you will need to file Form 8868.
                </Alert> */}
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Here&apos;s a detailed breakdown of deadlines based on the
                  business type:
                </Typography>
                <Box component="div" sx={{ mb: 1 }}>
                  <TableContainer
                    sx={{
                      border: '1px solid #828282',
                      borderCollapse: 'collapse',
                    }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'Top',
                            }}
                          >
                            <ul>
                              <li style={{ marginBottom: 16 }}>Form 1042</li>
                              <li style={{ marginBottom: 16 }}>
                                {' '}
                                <Link
                                  href="/resources/business-tax/form-1065-instructions/"
                                  sx={{ color: '#0202ff' }}
                                >
                                  {' '}
                                  Form 1065
                                </Link>
                              </li>
                              <li style={{ marginBottom: 16 }}>Form 1066</li>
                              <li style={{ marginBottom: 16 }}>
                                {' '}
                                <Link
                                  href="/resources/business-tax/form-1120-s-instructions/"
                                  sx={{ color: '#0202ff' }}
                                >
                                  Form 1120-S{' '}
                                </Link>{' '}
                              </li>
                              <li style={{ marginBottom: 16 }}>Form 3520-A</li>
                              <li style={{ marginBottom: 16 }}>Form 8804</li>
                            </ul>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: 400,
                              borderBottom: '1px solid #828282',

                              textAlign: 'center',
                              lineBreak: 1.5,
                            }}
                          >
                            <Typography variant="body1" sx={{ pb: 1 }}>
                              These forms are due by{' '}
                              <b style={{ color: '	#FFA500' }}>March 16, 2026</b>
                              .
                            </Typography>

                            <Typography variant="body1" sx={{ pb: 2 }}>
                              If you want more time to prepare and file these
                              tax forms, you can file an extension.
                            </Typography>

                            <Button
                              variant="contained"
                              sx={{
                                textTransform: 'none',
                                boxShadow: 'none',
                                borderRadius: 2,
                                backgroundColor: '#213e99',
                                mb: 1,
                                color: '#fff',
                                '&:hover, &:focus, &:active': { color: '#fff' },
                              }}
                              size="large"
                              href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=7004duedates`}
                            >
                              E-file Form 7004
                            </Button>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              Your extended deadline will be{' '}
                              <b style={{ color: '	#1da94a' }}>
                                September 15, 2026
                              </b>
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ lineHeight: 1.3 }}
                            >
                              (When you e-file Form 7004 with TaxZerone, you
                              will receive a reminder before the extended
                              deadline)
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                            }}
                          >
                            <ul>
                              <li style={{ marginBottom: 16 }}>
                                Form 1041 (estate other than a bankruptcy
                                estate)
                              </li>
                              <li style={{ marginBottom: 16 }}>
                                Form 1041 (trust)
                              </li>
                            </ul>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: 400,
                              borderBottom: '1px solid #828282',

                              textAlign: 'center',
                              lineBreak: 1.5,
                            }}
                          >
                            <Typography variant="body1" sx={{ pb: 1 }}>
                              These forms are due by{' '}
                              <b style={{ color: '	#FFA500' }}>April 15, 2026</b>
                              .
                            </Typography>

                            <Typography variant="body1" sx={{ pb: 2 }}>
                              If you need extra time to prepare and file these
                              tax forms, you can request an extension.
                            </Typography>

                            <Button
                              variant="contained"
                              sx={{
                                textTransform: 'none',
                                boxShadow: 'none',
                                borderRadius: 2,
                                backgroundColor: '#213e99',
                                mb: 1,
                                color: '#fff',
                                '&:hover, &:focus, &:active': { color: '#fff' },
                              }}
                              size="large"
                              href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=7004duedates`}
                            >
                              E-file Form 7004
                            </Button>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              Your extended deadline will be{' '}
                              <b style={{ color: '	#1da94a' }}>
                                September 30, 2026
                              </b>
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              width: 300,
                              borderRight: '1px solid #828282',

                              verticalAlign: 'top',
                            }}
                          >
                            <ul>
                              <li style={{ marginBottom: 16 }}>
                                Form 706-GS(T)
                              </li>
                              <li style={{ marginBottom: 16 }}>
                                Form 1041 (bankruptcy estate only)
                              </li>

                              <li style={{ marginBottom: 16 }}>Form 1041-N</li>
                              <li style={{ marginBottom: 16 }}>
                                Form 1041-QFT
                              </li>
                              <li style={{ marginBottom: 16 }}>Form 1120</li>

                              <li style={{ marginBottom: 16 }}>Form 1120-F</li>
                              <li style={{ marginBottom: 16 }}>
                                Form 1120-FSC
                              </li>
                              <li style={{ marginBottom: 16 }}>Form 1120-H</li>
                              <li style={{ marginBottom: 16 }}>Form 1120-L</li>
                              <li style={{ marginBottom: 16 }}>Form 1120-ND</li>
                              <li style={{ marginBottom: 16 }}>
                                Form 1120-ND (section 4951 taxes)
                              </li>
                              <li style={{ marginBottom: 16 }}>Form 1120-PC</li>
                              <li style={{ marginBottom: 16 }}>
                                Form 1120-POL
                              </li>
                              <li style={{ marginBottom: 16 }}>
                                Form 1120-REIT
                              </li>
                              <li style={{ marginBottom: 16 }}>
                                Form 1120-RIC
                              </li>
                              <li style={{ marginBottom: 16 }}>Form 1120-SF</li>
                            </ul>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: 400,
                              textAlign: 'center',
                              verticalAlign: 'top',
                            }}
                          >
                            <Typography variant="body1" sx={{ pb: 1 }}>
                              These forms are due by{' '}
                              <b style={{ color: '	#FFA500' }}>April 15, 2026</b>
                              .
                            </Typography>

                            <Typography variant="body1" sx={{ pb: 2 }}>
                              If you want more time to prepare and file these
                              tax forms, you can file an extension.
                            </Typography>

                            <Button
                              variant="contained"
                              sx={{
                                textTransform: 'none',
                                boxShadow: 'none',
                                borderRadius: 2,
                                backgroundColor: '#213e99',
                                color: '#fff',
                                mb: 1,
                                '&:hover, &:focus, &:active': { color: '#fff' },
                              }}
                              size="large"
                              href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=7004duedates`}
                            >
                              E-file Form 7004
                            </Button>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              Your extended deadline will be{' '}
                              <b style={{ color: '	#1da94a' }}>
                                October 15, 2026
                              </b>
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ lineHeight: 1.3 }}
                            >
                              (When you e-file Form 7004 with TaxZerone, you
                              will receive a reminder before the extended
                              deadline)
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  severity="info"
                  icon={false}
                  sx={{ mb: 4, mt: 2, borderLeft: '3px solid #0288d1' }}
                >
                  <b>Special Case: </b> <br style={{ marginBottom: 6 }} />
                  <span style={{ fontWeight: 600 }}>Form 1120-C:</span>{' '}
                  Cooperatives described in section 6072(d) must file by the
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    15th day of the 9th month
                  </span>{' '}
                  after their tax year ends. All other cooperatives must file by
                  the{' '}
                  <span style={{ fontWeight: 600 }}>
                    15th day of the 4th month
                  </span>{' '}
                  following the end of their tax year.
                </Alert>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 7004 deadline for businesses that operate on a fiscal tax
                  year:
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  For businesses operating on a fiscal tax year or with
                  non-calendar year-end dates, the deadline for filing Form 7004
                  is the 15th day of the third month following the close of the
                  tax year. For example, if a business&apos;s fiscal year ends
                  on June 30th, the deadline for Form 7004 would be September
                  15th. These businesses can also{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-7004-business-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 7004
                  </Link>{' '}
                  to extend the deadline.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to File Form 7004 for the 2025 Tax Year?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Businesses can file Form 7004 through two main methods:
                  electronic filing (e-filing) or traditional paper filing.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  E-filing Form 7004
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  E-filing is an efficient method to submit 7004 extension form.
                  It is also recommended by the IRS for accuracy and quick
                  processing time. Businesses can{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-7004-business-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    e-file Form 7004
                  </Link>{' '}
                  using an IRS-authorized e-file provider by providing the
                  required information and receive confirmation of their
                  extension request.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For those who prefer the traditional route, paper filing is
                  still an option. Businesses can download Form 7004 from the
                  official IRS website, complete the form manually, and mail it
                  to the IRS
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  To learn how to fill out Form 7004, refer to our{' '}
                  <Link
                    href="/resources/tax-extension/form-7004-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 7004 instructions
                  </Link>
                  .
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> If you are unsure about mailing addresses, check
                  out the section{' '}
                  <Link
                    href="/resources/tax-extension/form-7004-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    where do I mail Form 7004
                  </Link>{' '}
                  for detailed guidance.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Penalties for Form 7004
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }} component="div">
                  The IRS imposes penalties for businesses that miss paying
                  taxes and filing income tax returns
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Late filing penalty:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you file your tax return after the deadline (even with an
                  extension), the IRS may charge a penalty unless you have a
                  valid reason.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Late payment penalty:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If you don’t pay your taxes on time, a penalty of{' '}
                    <span style={{ fontWeight: 600 }}> 0.5% per month</span> is
                    charged on the unpaid amount,{' '}
                    <span style={{ fontWeight: 600 }}>up to 25%.</span> This may
                    be waived if you have a valid reason.
                  </li>
                  <li>
                    For corporations, no penalty applies if at least 90% of the
                    tax is paid by the original due date and the rest by the
                    extended deadline.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Interest:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Interest is charged on unpaid taxes from the original due date
                  until fully paid, even if you have an extension.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Choose TaxZerone to e-file Form 7004
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  With TaxZerone, you can complete your Form 7004 filing in just
                  3 simple steps.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Step 1: </b>Select the tax year and fill out the tax
                  return.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Step 2: </b>Calculate the balance due by providing total
                  tentative tax and payments made. Enter the bank account
                  details for balance due payment.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Step 3: </b>Transmit the return to the IRS. You will
                  receive an email once the IRS approves the return.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  When you have all the information, you can then follow the
                  steps below to e-file Form 7004 using TaxZerone.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              E-file your Form 7004 easily with TaxZerone <br />
              at just <b>$17.99/return</b>.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=7004duedates`}
              >
                E-file Form 7004
              </Button>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Get an extension of 6 months
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form7004DueDate;
